import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { ApiDetails, KEY } from '../dummyData';
import CryptoJS from 'crypto-js';

// Fail-open defaults — every feature is ON until the API says otherwise.
const DEFAULT_FLAGS = {
    dashboard: true,
    profile: true,
    profile_personal_info: true,
    profile_lease_details: true,
    profile_documents: true,
    profile_id_verification: true,
    agreements: true,
    support_tickets: true,
    support_tickets_view: true,
    support_tickets_create: true,
    messages: true,
    loans: true,
    payments: true,
};

const FeatureFlagsContext = createContext({
    flags: DEFAULT_FLAGS,
    messages: {},
    loading: false,
    isEnabled: () => true,
    getMessage: () => null,
    refresh: () => {},
});

export const FeatureFlagsProvider = ({ children }) => {
    const [flags, setFlags] = useState(DEFAULT_FLAGS);
    const [messages, setMessages] = useState({});
    const [loading, setLoading] = useState(false);
    // tenantId is set once flags are fetched successfully; drives the SSE effect.
    const [tenantId, setTenantId] = useState(null);

    const fetchFlags = useCallback(async () => {
        try {
            const encryptedDetails = localStorage.getItem('tenant_details');
            if (!encryptedDetails) return; // Not logged in — keep defaults.

            const decrypted = CryptoJS.AES.decrypt(encryptedDetails, KEY);
            const originalText = decrypted.toString(CryptoJS.enc.Utf8);
            if (!originalText) return;

            const tenantDetails = JSON.parse(originalText);

            // Expose the tenant ID so the SSE effect can connect.
            if (tenantDetails.tenant_id) {
                setTenantId(tenantDetails.tenant_id);
            }

            setLoading(true);

            const response = await fetch(
                ApiDetails + 'pegasus/visionary/tenant/app/features',
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        token: tenantDetails.access_token,
                    },
                }
            );

            if (response.ok) {
                const data = await response.json();
                if (data.success && data.features) {
                    // Merge: unknown/missing keys default to true (fail-open).
                    setFlags({ ...DEFAULT_FLAGS, ...data.features });
                }
                if (data.messages) {
                    setMessages(data.messages);
                }
            }
            // Any non-OK response → keep defaults (fail-open).
        } catch {
            // Network failure or parse error → keep defaults (fail-open).
        } finally {
            setLoading(false);
        }
    }, []);

    // Initial fetch on mount (no-op when not logged in).
    useEffect(() => {
        fetchFlags();
    }, [fetchFlags]);

    // SSE listener: re-fetches flags immediately when the backend pushes
    // a "features_updated" event via /pegasus/visionary/sse/{tenant_id}.
    useEffect(() => {
        if (!tenantId) return;

        let eventSource;
        try {
            eventSource = new EventSource(
                ApiDetails + 'pegasus/visionary/sse/' + tenantId
            );

            eventSource.addEventListener('features_updated', () => {
                fetchFlags();
            });

            // On error, close — browser will not auto-reconnect a closed source.
            // The next login / refresh will re-establish the connection.
            eventSource.onerror = () => {
                eventSource.close();
            };
        } catch {
            // SSE not supported or URL invalid — fail silently.
        }

        return () => {
            if (eventSource) {
                eventSource.close();
            }
        };
    }, [tenantId, fetchFlags]);

    const isEnabled = useCallback(
        (featureKey) => {
            return flags[featureKey] !== false; // Unknown keys default to true.
        },
        [flags]
    );

    const getMessage = useCallback(
        (featureKey) => messages[featureKey] || null,
        [messages]
    );

    return (
        <FeatureFlagsContext.Provider value={{ flags, messages, loading, isEnabled, getMessage, refresh: fetchFlags }}>
            {children}
        </FeatureFlagsContext.Provider>
    );
};

export const useFeatureFlags = () => useContext(FeatureFlagsContext);

export default FeatureFlagsContext;
